import React from 'react';
import { LocalPositionWithMarket } from '../../../types';
import { useSettings } from '../../../lib/settings/settingsContext.tsx';

interface AdminUserPositionsTableProps {
  positions: LocalPositionWithMarket[];
}

export function AdminUserPositionsTable({ positions }: AdminUserPositionsTableProps) {
  const { formatCurrency } = useSettings();

  if (!positions.length) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500">
        No positions found for this user.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <HeaderCell>Market</HeaderCell>
              <HeaderCell>Outcome</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell className="text-right">Stake</HeaderCell>
              <HeaderCell className="text-right">Potential Win</HeaderCell>
              <HeaderCell className="text-right">Odds</HeaderCell>
              <HeaderCell className="text-right">Placed</HeaderCell>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {positions.map((position) => {
              const odds = position.currentOdds ?? 0;
              return (
                <tr key={position.betId} className="hover:bg-gray-50">
                  <BodyCell>
                    <div className="font-medium text-gray-900">{position.marketTitle}</div>
                    <div className="text-xs text-gray-500">{position.marketCategory}</div>
                  </BodyCell>
                  <BodyCell>{position.option_name}</BodyCell>
                  <BodyCell>
                    <StatusBadge status={position.status} />
                  </BodyCell>
                  <BodyCell align="right">{formatCurrency(position.amountAfterFee)}</BodyCell>
                  <BodyCell align="right">{formatCurrency(position.currentPotentialWin ?? 0)}</BodyCell>
                  <BodyCell align="right">{odds ? odds.toFixed(2) : '—'}</BodyCell>
                  <BodyCell align="right">
                    {position.betTime ? new Date(position.betTime).toLocaleString() : '—'}
                  </BodyCell>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface HeaderCellProps {
  children: React.ReactNode;
  className?: string;
}

function HeaderCell({ children, className }: HeaderCellProps) {
  return (
    <th
      scope="col"
      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 ${className || ''}`}
    >
      {children}
    </th>
  );
}

interface BodyCellProps {
  children: React.ReactNode;
  align?: 'left' | 'right';
}

function BodyCell({ children, align = 'left' }: BodyCellProps) {
  return (
    <td className={`px-4 py-3 whitespace-nowrap text-sm text-gray-700 ${align === 'right' ? 'text-right' : ''}`}>
      {children}
    </td>
  );
}

interface StatusBadgeProps {
  status: LocalPositionWithMarket['status'];
}

function StatusBadge({ status }: StatusBadgeProps) {
  const normalized = status || 'active';
  const color = getStatusClasses(normalized);
  const label = normalized.charAt(0).toUpperCase() + normalized.slice(1);

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${color}`}>{label}</span>
  );
}

function getStatusClasses(status: string) {
  switch (status) {
    case 'won':
      return 'bg-green-100 text-green-700';
    case 'lost':
      return 'bg-red-100 text-red-700';
    case 'cancelled':
      return 'bg-yellow-100 text-yellow-700';
    case 'active':
    default:
      return 'bg-blue-100 text-blue-700';
  }
}

